import { InjectionToken, Type } from '@angular/core';
import { OrArray } from '@datorama/akita';
import { ErrorHandlerService } from '@y42/error-handling';
import { GitQuery, GitService } from '@y42/git';
import { ensureArray } from '@y42/util/ensure-array';
import { debounceTime, filter, firstValueFrom } from 'rxjs';
import { AbstractStoreModel } from '../../models/abstract-store-model.model';
import { FeatureGitClient } from '../client/feature-git-client.service';

export const AllowedModelFileNames = ['metadata', 'settings'] as const;

export type DefaultGitFileName = (typeof AllowedModelFileNames)[0];
export type FilesDef<T extends string = DefaultGitFileName> = Record<
  T,
  {
    fileName: `${T}.json`;
    model?: Type<object>;
    serialize?: boolean;
  }
>;
type DefaultFilesDef = FilesDef<DefaultGitFileName> & Partial<FilesDef<'settings'>>;

export type GitParentFolders = string[] | undefined;
export const GIT_PARENT_FOLDERS = new InjectionToken<GitParentFolders>('Git Parent Folders');

export interface AbstractGitServiceConfig<Files extends DefaultFilesDef = DefaultFilesDef> {
  name: string;
  basePath: string;
  gitQuery: GitQuery;
  git: GitService;
  parentFolders: GitParentFolders;
  errorHandlerService: ErrorHandlerService;
  models?: Files;
}

export abstract class AbstractGitService<
  StoreModel extends AbstractStoreModel = AbstractStoreModel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Folder extends string = string,
  Files extends DefaultFilesDef = DefaultFilesDef,
> {
  constructor(protected readonly cfg: AbstractGitServiceConfig<Files>) {}

  private readonly _parentFolders = this.cfg.parentFolders;
  protected readonly gitQuery = this.cfg.gitQuery;
  protected readonly gitClient = this.cfg.git as FeatureGitClient;

  async create(model: StoreModel): Promise<unknown> {
    if (!this.cfg.models?.metadata.model) {
      throw new Error(`[${this.cfg.name}]: missing metadata git model`);
    }

    await this.gitClient.createFolder(this.getPath(model.$name), { skipSync: true, emitFilesChangedEvent: true });

    return Promise.all(
      Object.entries(this.cfg.models).map(([_, { fileName, serialize, model: clazz }]) => {
        const file = {
          path: this.getPath(model.$name, fileName),
          content: serialize ? JSON.parse(JSON.stringify(new clazz!(model))) : new clazz!(model),
        };
        return this.gitClient.scheduleWriteFile(file);
      }),
    );
  }

  async delete($names: OrArray<StoreModel['$name']>): Promise<void> {
    $names = ensureArray($names);
    if (this.cfg.models) {
      await this.gitClient.deleteDirectory($names.map((name) => this.getPath(name)));
    } else {
      await this.gitClient.deleteFile($names.map((name) => ({ path: this.getPath(name) })));
    }
  }

  protected async getModels(
    options: {
      ignoredFolderNames?: string[];
    } = {},
  ): Promise<StoreModel[]> {
    if (!this.cfg.models?.metadata) {
      throw new Error(`[${name}]: missing models definition`);
    }

    const { ignoredFolderNames = [] } = options;
    const path = this.getPath();
    const dirExists = this.checkIfFolderExists(path);

    if (!dirExists) {
      return [];
    }

    const fileTree = this.gitQuery.getTreeItemAtPath(path);
    const children = Object.values(fileTree?.children || {})
      .filter(({ type }) => type === 'dir')
      .filter(({ name }) => name !== '.y42')
      .filter(({ name }) => !ignoredFolderNames.includes(name))
      .filter((dir) => !!dir.children);

    if (!children.length) {
      return [];
    }

    const models = (await this.gitClient.readGitStoreModels({ path })) as StoreModel[];
    return models;
  }

  protected async getModel($name: StoreModel['$name']): Promise<StoreModel | null> {
    if (!this.cfg.models?.metadata) {
      throw new Error(`[${name}]: missing models definition`);
    }

    const path = this.getPath($name);
    return (await this.gitClient.readGitStoreModel({ path })) as StoreModel | null;
  }

  public getPath(id?: StoreModel['$name'], fileName?: string) {
    let path = `${this.cfg.basePath}`;

    const parentFolders = this._parentFolders;
    if (parentFolders?.length) {
      path = `${path}/${parentFolders.join('/')}`;
    }

    if (id) {
      path = `${path}/${id}`;
    }

    if (fileName) {
      path = `${path}/${fileName}`;
    }

    return path;
  }

  public async readJSON<T>(path: string, cwd?: string): Promise<T | null> {
    return (await this.gitClient.readJSON<T>(path, cwd)) as T;
  }

  public async readFile(path: string, cwd?: string): Promise<string | null> {
    return await this.gitClient.readFile(path, cwd);
  }

  public checkIfFolderExists(folder: string): boolean {
    const dirExists = this.gitQuery.isDir(folder);
    if (!dirExists) {
      console.warn(`[${this.cfg.name}]: could not find folder "${folder}"`);
      return false;
    }

    return true;
  }

  public checkIfFileExists(path: string): boolean {
    return this.gitQuery.elementAtPathExists(path);
  }

  /**
   * Stages & commits all changed files under the currently active entity
   */
  async commitChanges(message: string, idOrFiles: StoreModel['$path'] | string[]) {
    // unfortunately, when creating and deleting and updating tables
    // race conditions occur, and the next part continues
    // before the GitStore has been updated.
    // So we need to wait for the status to become `null` before continuing,
    // otherwise the commit will fail because no changed files are registered yet.
    //
    // TODO the GitService needs refactoring to support batch processing of multiple file operations
    await firstValueFrom(
      this.gitQuery.gitStatusCode$.pipe(
        filter((status) => status === null),
        debounceTime(150),
      ),
    );

    const changedFiles = Array.isArray(idOrFiles)
      ? idOrFiles
      : this.gitQuery.getModifiedFilesList().filter((path) => path.startsWith(idOrFiles));

    try {
      await this.gitClient.commit(message, changedFiles);
    } catch (err) {
      this.cfg.errorHandlerService.error('[AbstractGitService]: Could not commit the changes', {
        err,
        toast: 'Could not commit the changes',
      });

      throw err;
    }
  }
}
